import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BulkStepLayout from "../../pages/BulkStepLayout";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, IconButton, Backdrop, CircularProgress } from "@mui/material";
import { DashCircleFill } from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { useItems } from "../../features/Fetch/Items";
import { BudgetItem, BudgetType, CurrentUser, DateTime, FamilyBucketLabel, TimeframeItem, UserItem } from "../../types";
import Description from "../Inputs/Description";
import Amount from "../Inputs/Amount";
import Dropdown from "../Inputs/Dropdown";
import DateInput from "../Inputs/Date";
import { SelectableItem } from "../../types";

const createEmptyBudget = (users: UserItem[]): BudgetItem => ({
  budgetId: 0,
  description: "",
  variant: {
    budgetVariantId: 0,
    budgetId: 0,
    amount: 0,
	incomeAmount: 0,
    startDate: new DateTime().startOfYear(),
    endDate: null,
    defaultEndDate: new DateTime(),
    isDefault: true,
    isInactive: false
  },
  timeframeId: -2,
  userIds: users?.map(u => u.userAccountId) ?? [],
  color: "#00619b",
  requiresApproval: false,
  isViewTransactions: true,
  budgetType: BudgetType.Expense,
  isMine: false,
  isInactive: false,
  amountSpent: 0,
  amountRemaining: 0,
  activeTimeframes: [],
  bucketNodes: []
});

const BulkBudgetEditor = (props: any) => {
  const { step, onNext, onBack, steps } = props;
  const repo = useItems();
  const navigate = useNavigate();
  const budgets: BudgetItem[] = useSelector((state: any) => state.budgets.value);
  const timeframes: TimeframeItem[] = useSelector((state: any) => state.timeframes.value);
  const users: UserItem[] = useSelector((state: any) => state.users.value);
  const user: CurrentUser = useSelector((state: any) => state.userData.value);
  const budgetLabel = FamilyBucketLabel(user, "budget", "Budget");
  const [rows, setRows] = useState<BudgetItem[]>([]);
  const [originalRows, setOriginalRows] = useState<BudgetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfirmExit, setShowConfirmExit] = useState(false);

  useEffect(() => {
    if (budgets && timeframes && users) {
      setRows([...budgets, createEmptyBudget(users)]);
      setOriginalRows([...budgets]);
      setLoading(false);
    }
  }, [budgets, timeframes, users]);

  if (loading || !budgets || !timeframes || !users) return <p>Loading...</p>;

  return (
    <BulkStepLayout step={step} onNext={onNext} onBack={onBack} steps={steps}>
      <Paper elevation={0} style={{ padding: 8, backgroundColor: 'azure', paddingBottom: 64 }}>
        <h2 className="bulk-category-title">Bulk {budgetLabel} Editor</h2>
        <TableContainer style={{overflow: "unset"}}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className="compact-table-cell header">Description</TableCell>
                <TableCell className="compact-table-cell header">Spending {budgetLabel}</TableCell>
                <TableCell className="compact-table-cell header">Income {budgetLabel}</TableCell>
                <TableCell className="compact-table-cell header">Frequency</TableCell>
                <TableCell className="compact-table-cell header narrow-table-cell">Activation Date</TableCell>
                <TableCell className="compact-table-cell header" sx={{ width: 128}}>Require Approval</TableCell>
                <TableCell className="compact-table-cell header" sx={{ width: 160}}>{budgetLabel} Type</TableCell>
                <TableCell className="compact-table-cell bottomless" sx={{ width: 32 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <React.Fragment key={idx}>
                  <TableRow sx={{ backgroundColor: idx % 2 === 1 ? '#ddffff' : 'inherit' }}>
                    <TableCell className="compact-table-cell">
                      <Description
                        NoLabel
                        NoMB
                        Value={row.description}
                        Variant="standard"
                        OnChange={(val: React.ChangeEvent<HTMLInputElement>) => handleCellChange(idx, "description", val.target.value)}
                        Size={12}
                      />
                    </TableCell>
                    <TableCell className="compact-table-cell">
                      <Amount
                        NoLabel
                        NoMB
                        Variant="standard"
                        Size={12}
                        Value={row.variant.amount}
                        Disabled={rows.length <= 1 || idx === rows.length - 1 || row.budgetType === BudgetType.Income}
                        OnChange={(val: React.ChangeEvent<HTMLInputElement>) => handleCellChange(idx, "variant", { ...row.variant, amount: parseFloat(val.target.value) })}
                      />
                    </TableCell>
					<TableCell className="compact-table-cell">
						<Amount
							NoLabel
							NoMB
							Variant="standard"
							Size={12}
							Value={row.variant.incomeAmount}
                        Disabled={rows.length <= 1 || idx === rows.length - 1 || row.budgetType === BudgetType.Expense}
							OnChange={(val: React.ChangeEvent<HTMLInputElement>) => handleCellChange(idx, "variant", { ...row.variant, incomeAmount: parseFloat(val.target.value) })}
						/>
					</TableCell>
                    <TableCell className="compact-table-cell">
                      {rows.length <= 1 || idx === rows.length - 1 ? (
                      <Description
                        NoLabel
                        NoMB
                        Value=""
                        Variant="standard"
                        Disabled
                        Size={12}
                      />
                      ) : (
                        <Dropdown
                          Size={12}
                          NoMB
                          Variant="standard"
                          Options={timeframes.map(t => ({ Id: t.timeframeId, Value: t.longDescription } as SelectableItem))}
                          Index={0}
                          Value={row.timeframeId}
                          OnChange={(val: number) => handleCellChange(idx, "timeframeId", val)}
                          EnableNegative
                          Disabled={row.budgetId !== 0 || rows.length <= 1 || idx === rows.length - 1}
                        />
                      )}
                    </TableCell>
                    <TableCell className="compact-table-cell narrow-table-cell">
                      {row.budgetId !== 0 || rows.length <= 1 || idx === rows.length - 1 ? (
                      <Description
                        NoLabel
                        NoMB
                        Value={row.activeTimeframes && row.activeTimeframes.length > 0 && row.activeTimeframes[row.activeTimeframes.length - 1]?.startDate
                            ? row.activeTimeframes[row.activeTimeframes.length - 1]?.startDate?.toString()
                            : ""}
                        Variant="standard"
                        Disabled
                        Size={12}
                      />
                      ) : (
                        <DateInput
                          Size={12}
                          NoMB
                          Borderless
                          OnChange={(val: string | Date) => handleCellChange(idx, "variant", { ...row.variant, startDate: new DateTime(val) })}
                          Value={row.variant.startDate}
                          NoError
                        />
                      )}
                    </TableCell>
                    <TableCell className="compact-table-cell">
                      {rows.length <= 1 || idx === rows.length - 1 ? (
                      <Description
                        NoLabel
                        NoMB
                        Value=""
                        Variant="standard"
                        Disabled
                        Size={12}
                      />
                      ) : (
                        <Dropdown
                          Size={12}
                          NoMB
                          Variant="standard"
                          Options={[
                            { Id: true, Value: "Yes" },
                            { Id: false, Value: "No" }
                          ]}
                          Value={row.requiresApproval}
                          OnChange={(val: boolean) => handleCellChange(idx, "requiresApproval", val)}
                          Disabled={rows.length <= 1 || idx === rows.length - 1}
                        />
                      )}
                    </TableCell>
                    <TableCell className="compact-table-cell">
                      {rows.length <= 1 || idx === rows.length - 1 ? (
                      <Description
                        NoLabel
                        NoMB
                        Value=""
                        Variant="standard"
                        Disabled
                        Size={12}
                      />
                      ) : (
                        <Dropdown
                          Size={12}
                          NoMB
                          Variant="standard"
                          Options={[
                            { Id: BudgetType.Expense, Value: "Expense" },
							{ Id: BudgetType.Income, Value: "Income" },
							{ Id: BudgetType.Combined, Value: "Combined" }
                          ]}
                          Value={row.budgetType}
                          OnChange={(val: BudgetType) => handleBudgetTypeChange(idx, val)}
                          Disabled={rows.length <= 1 || idx === rows.length - 1}
                        />
                      )}
                    </TableCell>
                    <TableCell className="compact-table-cell bottomless" style={{ width: 32, backgroundColor: 'azure' }}>
                      {rows.length > 1 && idx !== rows.length - 1 && row.budgetId === 0 && (
                        <IconButton 
                          size="small" 
                          color="error" 
                          tabIndex={-1} 
                          onClick={() => handleDeleteRow(idx)}>
                          <DashCircleFill />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <div className="bulk-budget-action-row">
          <Button
            variant="outlined"
            color="error"
            onClick={handleExitWithoutSaving}
            style={{ minWidth: 160, padding: '4px 16px' }}
          >
            Exit Without Saving
          </Button>
          <div className="bulk-budget-action-buttons">
            {hasChangesToSave() && (
              <Button
                variant="contained"
                color="success"
                onClick={handleSaveAndExit}
                style={{ minWidth: 120, padding: '4px 16px' }}
              >
                Save and Exit
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={hasChangesToSave() ? handleSaveAndContinue : onNext}
              style={{ minWidth: 120, padding: '4px 16px' }}
            >
              {hasChangesToSave() ? 'Save and Continue' : 'Continue'}
            </Button>
          </div>
        </div>
        {showConfirmExit && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Exit Without Saving?</h3>
              <p>Are you sure you want to exit without saving your changes?</p>
              <div className="modal-actions">
                <Button variant="contained" color="error" onClick={handleConfirmExit}>Exit</Button>
                <Button variant="outlined" onClick={handleCancelExit}>Cancel</Button>
              </div>
            </div>
          </div>
        )}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading || saving}>
        <CircularProgress />
      </Backdrop>
      </Paper>
    </BulkStepLayout>
  );

  function handleCellChange(index: number, field: string, value: any) {
    const updatedRows = rows.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    );
    // If editing the last row, add a new empty row
    if (index === rows.length - 1 && !isRowEmpty(updatedRows[index])) {
      updatedRows.push(createEmptyBudget(users));
    }
    setRows(updatedRows);
  }

  function handleBudgetTypeChange(index: number, newType: BudgetType) {
    const updatedRows = rows.map((row, i) => {
      if (i !== index) return row;

      const amount = row.variant?.amount ?? 0;
      const incomeAmount = row.variant?.incomeAmount ?? 0;
      const nextVariant = { ...row.variant };

      if (newType === BudgetType.Income) {
        // Prefer keeping existing incomeAmount; otherwise migrate amount over
        nextVariant.incomeAmount = incomeAmount > 0 ? incomeAmount : amount;
        nextVariant.amount = 0;
      } else if (newType === BudgetType.Expense) {
        // Prefer keeping existing amount; otherwise migrate incomeAmount over
        nextVariant.amount = amount > 0 ? amount : incomeAmount;
        nextVariant.incomeAmount = 0;
      } else {
        // Combined: keep both fields as-is
        nextVariant.amount = amount;
        nextVariant.incomeAmount = incomeAmount;
      }

      return { ...row, budgetType: newType, variant: nextVariant };
    });

    if (index === rows.length - 1 && !isRowEmpty(updatedRows[index])) {
      updatedRows.push(createEmptyBudget(users));
    }
    setRows(updatedRows);
  }

  function isRowEmpty(row: BudgetItem) {
    return !row.description && !row.variant.amount && !row.variant.incomeAmount && !row.timeframeId;
  }

  function hasChanged(row: BudgetItem) {
    const original = originalRows.find(r => r.budgetId === row.budgetId);
    return original && JSON.stringify(original) !== JSON.stringify(row);
  }

  function hasChangesToSave() {
    return (
      rows.slice(0, -1).some(row => {
        if (!row.budgetId && !isRowEmpty(row)) return true; // new row
        if (row.budgetId && !isRowEmpty(row) && hasChanged(row)) return true; // changed row
        return false;
      })
    );
  }

  async function handleSave() {
    setSaving(true);
    const updatedBudgets = rows
      .slice(0, -1)
      .filter(row => row.budgetId && !isRowEmpty(row) && hasChanged(row));
    const newBudgets = rows
      .slice(0, -1)
      .filter(row => !row.budgetId && !isRowEmpty(row));

    // Use bulk upsert API for new and updated budgets
    await repo.UpsertBudgetsAsync([...updatedBudgets, ...newBudgets]);

    // Reload all after save
    await repo.GetBudgetsAsync();
    setSaving(false);
  }

  async function handleSaveAndContinue() {
    await handleSave();
    onNext();
  }

  async function handleSaveAndExit() {
    await handleSave();
    navigate('/budgets');
  }

  function handleExitWithoutSaving() {
    if (hasChangesToSave()) {
      setShowConfirmExit(true);
    } else {
      navigate('/budgets');
    }
  }

  function handleConfirmExit() {
    setShowConfirmExit(false);
    navigate('/budgets');
  }

  function handleCancelExit() {
    setShowConfirmExit(false);
  }

  function handleDeleteRow(index: number) {
    // Prevent deleting the last (empty) row
    if (index === rows.length - 1) return;
    const updatedRows = rows.filter((_, i) => i !== index);
    setRows(updatedRows);
  }
};

export default BulkBudgetEditor;
